import { useCallback, useEffect, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime, rupiah } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { DatePicker } from "../components/DatePicker";

const STATUS_STYLE = {
  lunas: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  pending: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  gagal: "bg-rose-500/10 text-rose-400 border-rose-500/20",
};

export default function SalesReport({ type, title }) {
  const [data, setData] = useState({ sales: [], summary: {} });
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = useCallback(() => {
    api.get("/sales", { params: { type, date_from: from || undefined, date_to: to || undefined } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errMsg(e)));
  }, [type, from, to]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const rows = [["Tanggal", "Item", "Qty", "Harga", "Total", "Pembayaran", "Status", "Pelanggan", "Komisi"]];
    data.sales.forEach((s) => rows.push([
      s.created_at, s.item_name, s.qty, s.price, s.total, s.payment_method, s.status, s.customer,
      (s.commissions || []).map((c) => `${c.agent_code}:${c.amount}`).join("|"),
    ]));
    const blob = new Blob([rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `penjualan-${type}-${Date.now()}.csv`;
    a.click();
  };

  const { summary } = data;
  return (
    <div className="space-y-6" data-testid={`sales-report-${type}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{title}</h1>
          <p className="text-sm text-muted-foreground mt-1">{summary.count || 0} transaksi tercatat</p>
        </div>
        <Button variant="outline" onClick={exportCsv} data-testid="btn-export-csv"><Download className="w-4 h-4 mr-1.5" /> Export CSV</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Total Omset (Lunas)", value: rupiah(summary.omset), testid: "sum-omset" },
          { label: "Total Komisi Agen", value: rupiah(summary.komisi), testid: "sum-komisi" },
          { label: "Pending (Midtrans)", value: rupiah(summary.pending), testid: "sum-pending" },
          { label: "Jumlah Transaksi", value: summary.count || 0, testid: "sum-count" },
        ].map((k) => (
          <Card key={k.label} className="bg-card/60 border-border">
            <CardContent className="p-4">
              <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{k.label}</div>
              <div className="mt-1.5 text-xl font-extrabold font-mono" data-testid={k.testid}>{k.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-card/60 border-border">
        <CardContent className="p-4 flex flex-wrap gap-3 items-end">
          <div className="space-y-1.5"><Label>Dari Tanggal</Label><DatePicker value={from} onChange={(v) => setFrom(v)} testid="input-date-from" /></div>
          <div className="space-y-1.5"><Label>Sampai</Label><DatePicker value={to} onChange={(v) => setTo(v)} testid="input-date-to" /></div>
          <Button variant="secondary" onClick={() => { setFrom(""); setTo(""); }} data-testid="btn-reset-filter">Reset</Button>
        </CardContent>
      </Card>

      <Card className="bg-card border-border shadow-sm overflow-x-auto">
        <Table className="min-w-[760px]" data-testid="sales-table">
          <TableHeader>
            <TableRow>
              <TableHead>Tanggal</TableHead><TableHead>Item</TableHead><TableHead>Qty</TableHead><TableHead>Total</TableHead>
              <TableHead>Pembayaran</TableHead><TableHead>Status</TableHead><TableHead>Pelanggan</TableHead><TableHead>Komisi Agen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!data.sales.length && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground" data-testid="sales-empty">Belum ada transaksi</TableCell></TableRow>}
            {data.sales.map((s) => (
              <TableRow key={s.id} data-testid={`sale-row-${s.id}`}>
                <TableCell className="font-mono text-xs text-muted-foreground whitespace-nowrap">{fmtDateTime(s.created_at)}</TableCell>
                <TableCell>{s.item_name}{s.router_name ? <span className="block text-xs text-muted-foreground">{s.router_name}</span> : null}</TableCell>
                <TableCell className="font-mono">{s.qty}</TableCell>
                <TableCell className="font-mono font-medium">{rupiah(s.total)}</TableCell>
                <TableCell className="uppercase text-xs font-mono">{s.payment_method}</TableCell>
                <TableCell><span className={`px-2.5 py-0.5 rounded-full text-xs font-mono border ${STATUS_STYLE[s.status] || STATUS_STYLE.pending}`}>{s.status}</span></TableCell>
                <TableCell>{s.customer || "-"}</TableCell>
                <TableCell className="text-xs font-mono">
                  {(s.commissions || []).length
                    ? s.commissions.map((c, i) => <div key={i}>{c.agent_code}: {rupiah(c.amount)}</div>)
                    : "-"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
